import { useEffect, useState } from "react";
import { Button } from "@/ui/button";
import { Modal } from "@/ui/modals";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import FilterRow from "./filter-row";
import { MinMaxInput } from "@/ui/min-max-input";
import {
  PositionFilters,
  usePositionsFilter,
} from "@/providers/positions-filter/positions-filter";

const PositionsFiltersModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { filters, setFilters, clearFilters, defaultFilters } =
    usePositionsFilter();

  const [formData, setFormData] = useState(filters);

  useEffect(() => {
    if (isOpen) {
      setFormData(filters);
    }
  }, [isOpen, filters]);

  const updateFormData = <K extends keyof PositionFilters>(
    key: K,
    value: PositionFilters[K] | ""
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value !== "" ? value : undefined,
    }));
  };

  const handleDone = () => {
    setFilters(formData);
    setIsOpen(false);
  };

  const handleClear = () => {
    clearFilters();
    setFormData(defaultFilters);
    setIsOpen(false);
  };

  return (
    <>
      <GradientBorderRadius
        gradient="default-border"
        radius="bigger"
        hover="r-100"
        className="h-full"
      >
        <button
          onClick={() => setIsOpen(true)}
          className="w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center"
        >
          Filters
        </button>
      </GradientBorderRadius>

      <Modal
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        className="w-[28.125rem] py-[0.9375rem] px-[0.6250rem]"
      >
        <Modal.Title title="Filters" className="text-xs font-semibold" />
        <div className="w-full h-full grid grid-cols-[max-content,1fr] gap-y-[0.375rem] gap-x-[0.625rem] text-white text-xs font-semibold">
          <FilterRow title="Invested">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={formData.minInvested}
              maxValue={formData.maxInvested}
              onMinChange={(min) => updateFormData("minInvested", min)}
              onMaxChange={(max) => updateFormData("maxInvested", max)}
            />
          </FilterRow>
          <FilterRow title="Net PNL">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={formData.minNetPNL}
              maxValue={formData.maxNetPNL}
              onMinChange={(min) => updateFormData("minNetPNL", min)}
              onMaxChange={(max) => updateFormData("maxNetPNL", max)}
            />
          </FilterRow>
          <FilterRow title="Net ROI">
            <MinMaxInput
              unitSide="right"
              text="%"
              layout="row"
              minValue={formData.minNetROI}
              maxValue={formData.maxNetROI}
              onMinChange={(min) => updateFormData("minNetROI", min)}
              onMaxChange={(max) => updateFormData("maxNetROI", max)}
            />
          </FilterRow>
          <FilterRow title="Balance">
            <MinMaxInput
              layout="row"
              minValue={formData.minBalance}
              maxValue={formData.maxBalance}
              onMinChange={(min) => updateFormData("minBalance", min)}
              onMaxChange={(max) => updateFormData("maxBalance", max)}
            />
          </FilterRow>
          <FilterRow title="Time">
            <MinMaxInput
              unitSide="right"
              text="h"
              layout="row"
              minValue={formData.minTime}
              maxValue={formData.maxTime}
              onMinChange={(min) => updateFormData("minTime", min)}
              onMaxChange={(max) => updateFormData("maxTime", max)}
            />
          </FilterRow>
        </div>
        <Modal.Footer>
          <Button
            title="Done"
            active={false}
            className="w-[9.4375rem] font-semibold text-xs"
            onClick={handleDone}
          />

          <Button
            title="Clear"
            active={false}
            className="w-[9.4375rem] font-semibold text-xs"
            onClick={handleClear}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PositionsFiltersModal;
